import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, ExternalLink, ChevronDown, Menu, X, Code, Brain, Database, Cloud, Award, Briefcase, Bot, Car, Shield, Activity, Video } from 'lucide-react';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const experiences = [
    {
      title: "Python Developer Intern",
      company: "ConfideoIT Services",
      period: "Aug 2025 – Nov 2025",
      points: [
        "Developed Python-based backend logic for chatbot workflows using AWS Lex, Amazon Bedrock, Lambda, S3, and DynamoDB",
        "Designed and optimized serverless Lambda functions for intent handling and response generation",
        "Integrated Amazon Bedrock for LLM-powered contextual responses",
        "Built IDMS (Document Management System) with APIs, authentication, and CRUD operations"
      ]
    },
    {
      title: "ML & Data Analyst Intern",
      company: "Feynn Labs",
      period: "Jun 2025 – Aug 2025",
      points: [
        "Designed computer-vision-based inventory automation reducing manual errors by 85%",
        "Automated ETL and feature-engineering pipelines reducing prep time by 30%",
        "Conducted EDA and regression-based forecasting for demand prediction",
        "Built real-time KPI dashboards using Streamlit and Power BI"
      ]
    },
    {
      title: "R&D Intern",
      company: "DRDO CAIR, Govt. of India",
      period: "Oct 2024 – Jan 2025",
      points: [
        "Developed interpolation-based algorithms achieving 95% accuracy and 40% compute-time reduction",
        "Executed model testing, benchmarking, and statistical validation",
        "Documented workflows supporting iterative research and technical knowledge transfer"
      ]
    }
  ];

  const projects = [
    {
      title: "Hybrid AI Chatbot",
      tech: "Python, GPT4All, ChromaDB, GenAI",
      description: "Built an offline RAG-based chatbot for secure and cost-efficient information retrieval with embedding-based semantic search.",
      github: "#",
      icon: Bot,
      color: "from-cyan-400 to-blue-500",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80"
    },
    {
      title: "EV Revolution Analysis",
      tech: "PowerBI, SQL, Data Analysis",
      description: "Performed in-depth market segmentation and trend analysis for India's Electric Vehicle market with interactive visualizations.",
      github: "https://github.com/lalitshekhar/EV-Revolution-Analysis",
      icon: Car,
      color: "from-green-400 to-emerald-500",
      image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80"
    },
    {
      title: "Smart CCTV Safety System",
      tech: "YOLOv8, OpenCV, Computer Vision",
      description: "Real-time AI system monitoring workplace safety with custom YOLOv8 model detecting PPE compliance.",
      github: "#",
      icon: Shield,
      color: "from-orange-400 to-red-500",
      image: "https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=800&q=80"
    },
    {
      title: "Real-Time Pose Estimation",
      tech: "PyTorch, OpenCV, NLP",
      description: "Deep-learning keypoint detection achieving 98% accuracy with real-time camera streaming and gesture classification.",
      github: "#",
      icon: Activity,
      color: "from-purple-400 to-pink-500",
      image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800&q=80"
    },
    {
      title: "AI Model Video Comparison",
      tech: "Python, Deep Learning, MediaPipe",
      description: "Tool comparing multiple AI models visually with side-by-side inference and performance metrics.",
      github: "https://github.com/lalitshekhar/AI-Model-Video-Comparison",
      icon: Video,
      color: "from-yellow-400 to-orange-500",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
    }
  ];

  const skills = {
    "ML & Data": ["Machine Learning", "Deep Learning", "Computer Vision", "Data Analytics", "EDA", "Forecasting", "ETL Pipelines", "RAG"],
    "Programming": ["Python", "PySpark", "C/C++", "Java", "JavaScript", "SQL"],
    "Frameworks": ["TensorFlow", "PyTorch", "Scikit-learn", "OpenCV", "Pandas", "NumPy"],
    "Tools & Cloud": ["AWS", "Power BI", "Tableau", "Streamlit", "Git", "MongoDB"]
  };

  const stats = [
    { value: "85%", label: "Reduction in Manual Errors" },
    { value: "95%", label: "Algorithm Accuracy" },
    { value: "40%", label: "Performance Improvement" },
    { value: "25+", label: "Technologies Mastered" }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/95 backdrop-blur-lg shadow-lg shadow-cyan-500/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            </div>
            
            <div className="hidden md:flex space-x-8">
              {['Home', 'About', 'Experience', 'Projects', 'Skills', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`hover:text-cyan-400 transition-colors ${activeSection === item.toLowerCase() ? 'text-cyan-400' : ''}`}
                >
                  {item}
                </button>
              ))}
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-950/95 backdrop-blur-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['Home', 'About', 'Experience', 'Projects', 'Skills', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left px-3 py-2 hover:bg-slate-900 rounded"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-4 pt-16">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center space-y-6 animate-fade-in">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-4">
              Welcome to my AI/ML side 
            </div>
            <h1 className="text-5xl md:text-7xl font-bold">
              Hi Folks ,<span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent"> Myself P.Lalit</span>
              <br />
              <span className="text-amber-400">Shekhar</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-300">AI & ML Engineer</p>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
              Transforming Data into Intelligent Solutions
            </p>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Specialized in Computer Vision, Deep Learning, and AWS Cloud Solutions. Building intelligent systems that deliver measurable impact.
            </p>
            
            <div className="flex justify-center gap-4 pt-6">
              <a href="mailto:lalitshekhar20443@gmail.com" className="p-3 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded-full transition-all hover:scale-110">
                <Mail className="w-6 h-6" />
              </a>
              <a href="https://linkedin.com/in/lalitshekhar" target="_blank" rel="noopener noreferrer" className="p-3 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded-full transition-all hover:scale-110">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href="https://github.com/lalitshekhar" target="_blank" rel="noopener noreferrer" className="p-3 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 rounded-full transition-all hover:scale-110">
                <Github className="w-6 h-6" />
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="bg-slate-900/50 backdrop-blur-lg border border-cyan-500/20 rounded-2xl p-6 hover:bg-slate-900/70 hover:border-cyan-400/40 transition-all hover:scale-105">
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>

            <button onClick={() => scrollToSection('about')} className="animate-bounce mt-12">
              <ChevronDown className="w-8 h-8 text-cyan-400" />
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            About <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Me</span>
          </h2>
          <div className="bg-slate-900/50 backdrop-blur-lg border border-cyan-500/20 rounded-3xl p-8 md:p-12 hover:border-cyan-400/40 transition-all">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              AI & ML Engineer skilled in developing and deploying machine learning, computer vision, and predictive analytics solutions. Experienced in data preprocessing, model training, ETL automation, and dashboard development using Python, TensorFlow, PyTorch, and AWS.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              Delivered measurable results including 85% reduction in manual stock errors and 40% faster computation performance while improving data-driven decision-making across various industries.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div className="flex items-start gap-4">
                <Brain className="w-8 h-8 text-cyan-400 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-xl mb-2">Education</h3>
                  <p className="text-gray-400">B.Tech in AI & ML</p>
                  <p className="text-gray-500">KS Institute of Technology, Bengaluru (2021-2025)</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Award className="w-8 h-8 text-blue-400 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-xl mb-2">Certifications</h3>
                  <p className="text-gray-400">AWS Cloud Foundations, SnowFlake Pro Core</p>
                  <p className="text-gray-500">GenAI-Powered Analytics, Python Intermediate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            Work <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Experience</span>
          </h2>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div key={index} className="bg-slate-900/50 backdrop-blur-lg border border-cyan-500/20 rounded-3xl p-8 hover:bg-slate-900/70 hover:border-cyan-400/40 transition-all group">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-cyan-500/10 rounded-xl group-hover:bg-cyan-500/20 transition-all border border-cyan-500/20">
                    <Briefcase className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold">{exp.title}</h3>
                    <p className="text-cyan-400">{exp.company}</p>
                    <p className="text-gray-500 text-sm">{exp.period}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {exp.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <span className="text-cyan-400 mt-1">▹</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            Featured <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Projects</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => {
              const IconComponent = project.icon;
              return (
                <div key={index} className="relative bg-slate-900/50 backdrop-blur-lg border border-cyan-500/20 rounded-2xl overflow-hidden hover:bg-slate-900/70 hover:border-cyan-400/40 transition-all group">
                  {/* Animated Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Project Image Section */}
                  <div className="relative h-48 overflow-hidden">
                    {/* Background Image with Overlay */}
                    <div className="absolute inset-0 bg-slate-900">
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-110 transition-all duration-700"
                        loading="lazy"
                      />
                      {/* Dark Overlay for better contrast */}
                      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/90"></div>
                      {/* Colored Overlay */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-30 mix-blend-overlay`}></div>
                    </div>
                    
                    {/* Geometric Pattern Overlay */}
                    <div className="absolute inset-0 opacity-30" style={{
                      backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)`,
                      backgroundSize: '32px 32px'
                    }}></div>
                    
                    {/* Large Icon Display */}
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className={`p-8 bg-gradient-to-br ${project.color} rounded-3xl group-hover:scale-110 transition-transform duration-500 shadow-2xl backdrop-blur-sm`}>
                        <IconComponent className="w-20 h-20 text-white drop-shadow-lg" />
                      </div>
                    </div>
                    
                    {/* Animated Particles Effect */}
                    <div className="absolute top-4 left-4 w-2 h-2 bg-cyan-400 rounded-full animate-ping opacity-75 z-10"></div>
                    <div className="absolute top-8 right-8 w-2 h-2 bg-blue-400 rounded-full animate-ping opacity-75 z-10" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute bottom-6 left-1/3 w-2 h-2 bg-cyan-300 rounded-full animate-ping opacity-75 z-10" style={{animationDelay: '1s'}}></div>
                  </div>
                  
                  {/* Project Info Section */}
                  <div className="relative p-6">
                    <div className="flex items-center justify-between mb-3">
                      {/* Project Title with Gradient on Hover */}
                      <h3 className="text-xl font-bold group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-blue-400 group-hover:bg-clip-text group-hover:text-transparent transition-all">
                        {project.title}
                      </h3>
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors hover:scale-110 transform">
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                    
                    {/* Tech Stack Pills */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tech.split(', ').map((tech, idx) => (
                        <span key={idx} className="text-xs px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 hover:bg-cyan-500/20 transition-all">
                          {tech}
                        </span>
                      ))}
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-sm leading-relaxed">{project.description}</p>
                    
                    {/* Decorative Corner Element */}
                    <div className={`absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br ${project.color} rounded-full opacity-0 group-hover:opacity-20 blur-3xl transition-all duration-500`}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            Technical <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Skills</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(skills).map(([category, items], index) => (
              <div key={index} className="bg-slate-900/50 backdrop-blur-lg border border-cyan-500/20 rounded-3xl p-8 hover:bg-slate-900/70 hover:border-cyan-400/40 transition-all">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-2xl font-bold">{category}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill, idx) => (
                    <span key={idx} className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-sm hover:bg-cyan-500/20 hover:border-cyan-400/40 transition-all cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get In <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Touch</span>
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
          </p>
          <a
            href="mailto:lalitshekhar20443@gmail.com"
            className="inline-block px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full font-bold hover:shadow-lg hover:shadow-cyan-500/50 transition-all hover:scale-105"
          >
            Say Hello
          </a>
          <div className="flex justify-center gap-6 mt-12">
            <a href="mailto:lalitshekhar20443@gmail.com" className="text-gray-400 hover:text-cyan-400 transition-colors">
              lalitshekhar20443@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-cyan-500/20">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 text-lg italic mb-2">
            "The best way to predict the future is to create it."
          </p>
          <p className="text-gray-500 text-sm">
            Thank you for visiting. May you leave inspired to build something extraordinary.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;